/**
 * <s-product-gallery> — one-at-a-time product media gallery with prev/next arrows.
 *
 * Required markup:
 *   [data-product-gallery]        — horizontal snap-scrolling track; each direct child is one slide.
 *   button[data-gallery-prev]     — goes to the previous slide.
 *   button[data-gallery-next]     — goes to the next slide.
 *   [data-gallery-current]        — optional; receives the 1-based current slide number.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const track = this.#track;
    if (!track) return;

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.#go(-1), opts);
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.#go(1), opts);
    track.addEventListener('scroll', this.#onScroll, { ...opts, passive: true });
    this.addEventListener('keydown', this.#onKeydown, opts);
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get #track() {
    return this.querySelector('[data-product-gallery]');
  }

  get #slides() {
    return Array.from(this.#track?.children ?? []);
  }

  get #index() {
    const track = this.#track;
    if (!track || track.clientWidth === 0) return 0;
    return Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
  }

  #go(step) {
    const slides = this.#slides;
    if (slides.length < 2) return;
    // Wrap around so shoppers can keep clicking through every image.
    const next = (this.#index + step + slides.length) % slides.length;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    slides[next].scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
  }

  #onKeydown = (event) => {
    if (event.target instanceof HTMLInputElement) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    if (event.key === 'ArrowRight') this.#go(rtl ? -1 : 1);
    else if (event.key === 'ArrowLeft') this.#go(rtl ? 1 : -1);
  };

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #update() {
    const current = this.querySelector('[data-gallery-current]');
    if (current) current.textContent = String(Math.min(this.#index, this.#slides.length - 1) + 1);
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
